import { memo, useCallback, useState } from 'react'

interface FaviconProps {
  url: string | null | undefined
  className: string
  /** Rendered when the favicon is missing or fails to load; null hides it. */
  fallback?: React.ReactNode
}

/**
 * Favicon image with load-failure state: falls back to the given node (or
 * nothing) instead of a broken-image glyph. Failure is tracked per URL, so an
 * element reused with a different favicon (index-keyed rows, late-arriving
 * media) still attempts the new one.
 *
 * Memoized and lazy: long threads render hundreds of these in citation pills,
 * so offscreen ones must not fire network requests or re-render with the
 * message list.
 */
export const Favicon = memo(function Favicon({
  url,
  className,
  fallback,
}: FaviconProps) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null)

  const handleError = useCallback(() => setFailedUrl(url ?? null), [url])

  if (!url || failedUrl === url) return <>{fallback ?? null}</>

  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      decoding="async"
      className={className}
      onError={handleError}
    />
  )
})
